<template>
  <div class="d-cal-print text-dark">
    <ul class="d-cal-print__cal">
      <li class="d-cal-print__cal__day" v-for="weekDay in weekDays" :key="weekDay.date">
        <ul>
          <li class="d-cal-print__cal__day__header">{{ weekDay.formatted }}</li>
          <p class="text-explainer center" v-if="weekDay.events.length < 1">{{ $t('vue2.substitution.no_lessons') }}</p>
          <li class="d-cal-print__event"
              v-for="event in weekDay.events"
              :key="event.event_key"
              :class="{'d-cal-print__event--presence': event.event_type === 'presence'}"
              :style="{backgroundColor: event.color}">
            <div class="d-cal-print__event__header">
              <div class="d-cal-print__event__title">
                {{ event.title }}
              </div>
              <div class="d-cal-print__event__time">
                {{ event.event_starttime }}-{{ event.event_endtime }}
              </div>
            </div>
            <div class="day-cal__event__calendar" v-if="event.teacher"><span class="icon-user"></span>
              {{ event.teacher }}
            </div>
            <div class="day-cal__event__calendar" v-if="event.room"><span class="icon-location"></span>
              {{ event.room }}
            </div>
            <div class="day-cal__event__calendar" v-if="event.group"><span class="icon-users"></span>
              {{ event.group }}
            </div>
            <div class="day-cal__event__calendar" v-if="event.students"><span class="icon-users5"></span>
              {{ event.students }}
            </div>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script>
import moment from 'moment'

const I18N = {
  da: {
    no_lessons: 'Der er ingen lektioner i denne uge'
  },
  en: {
    no_lessons: 'No lessons this week'
  },
  nb: {
    no_lessons: 'Ingen leksjoner denne uken'
  }
}

export default {
  props: {
    calendarId: {
      default: 7
    },
    calendarName: {
      default: 'DCalendarList'
    },
    events: {
      type: Array,
      default: () => []
    },
    date: {
      default: () => moment()
    },
    isShowWeekends: {
      type: Boolean,
      default: false
    },
    firstDayOfWeek: {
      type: Number,
      default: 1
    },
    isCalendarInPrintMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale]
    },
    startDate () {
      return moment(this.date).isoWeekday(this.firstDayOfWeek)
    },
    endDate () {
      const endDayNum = this.isShowWeekends ? this.firstDayOfWeek + 6 : this.firstDayOfWeek + 4
      return moment(this.date).isoWeekday(endDayNum)
    },
    weekDays () {
      let days = []
      for (let i = this.firstDayOfWeek; i <= this.numberOfDays; i++) {
        const day = moment(this.date).isoWeekday(i)
        const events = this.eventsWithMetaData.filter(event => moment(event.start).format('YYYY-MM-DD') === day.format('YYYY-MM-DD'))
        days.push({
          date: day.format('YYYY-MM-DD'), // Format the date as desired (e.g., 'YYYY-MM-DD')
          formatted: this.getShowDate(day), // Customize the date format as needed,
          events: this.sortEvents(events)
        })
      }
      return days
    },
    numberOfDays () {
      if (this.isShowWeekends) {
        return this.firstDayOfWeek + 6
      } else {
        return this.firstDayOfWeek + 4
      }
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    userSchools () {
      return this.$store.getters.userSchools
    },
    eventsWithMetaData () {
      return this.events.map(originalLesson => {
        const lesson = { ...originalLesson }

        if (lesson.rendering) {
          Object.assign(lesson, {
            event_starttime: '',
            event_endtime: '',
            has_notes: false,
            has_substitute: false,
            group: '',
            teacher: '',
            v_teachers_arr: [],
            room: '',
            substitutes: '',
            eventDuration: 0,
            v_other_school: false,
            v_is_editable: false
          })
          return lesson
        }

        const stripHtml = str => (str || '').replace(/<(?:.|\n)*?>/gm, '')
        const hasNotes =
          stripHtml(lesson.description) ||
          stripHtml(lesson.notes_for_students) ||
          stripHtml(lesson.teacher_notes) ||
          stripHtml(lesson.learning_goal)

        const groups = []
        const allGroups = []
        const rooms = []
        const allRooms = []
        const teachers = []
        const allTeachers = []
        const students = []
        const allStudents = []
        const substitutes = []

        let hasSubstitutes = false
        let eventActiveCalendarDuration = 0

        lesson.calendars.forEach(calendar => {
          const calMeta = this.allCalendarsInHash[calendar.calendar_id]
          if (!calMeta) return

          const name = calMeta.name
          const isCurrent = calendar.calendar_id === this.calendarId

          if (isCurrent) {
            eventActiveCalendarDuration = calendar.minutes
            return
          }

          switch (calMeta.type) {
            case 'klass':
              allGroups.push(name)
              if (groups.length < 3) groups.push(name)
              break
            case 'room':
              allRooms.push(name)
              if (rooms.length < 3) rooms.push(name)
              break
            case 'user':
              if (calendar.substitute) {
                hasSubstitutes = true
                substitutes.push(name)
              } else {
                allTeachers.push(name)
                if (teachers.length < 3) teachers.push(name)
              }
              break
            case 'student':
              allStudents.push(name)
              if (students.length < 3) students.push(name)
              break
          }
        })

        const overflowLabel = (arr, allArr) => {
          const overflow = allArr.length - 3
          if (overflow > 0) arr.push('+' + overflow)
          return arr.join(', ')
        }

        Object.assign(lesson, {
          event_starttime: moment(lesson.start).locale(this.locale).format('LT'),
          event_endtime: moment(lesson.end).locale(this.locale).format('LT'),
          event_key: this.calendarId + '_' + lesson.event_id,
          has_notes: !!hasNotes,
          has_substitute: hasSubstitutes,
          group: overflowLabel(groups, allGroups),
          teacher: overflowLabel(teachers, allTeachers),
          room: overflowLabel(rooms, allRooms),
          substitutes: substitutes.join(', '),
          eventDuration: eventActiveCalendarDuration,
          v_other_school: this.getIsEventOtherSchool(lesson),
          v_is_editable: this.getIsEventEditable(lesson),
          v_teachers_arr: allTeachers
        })

        if (this.isShowStudents) {
          lesson.students = overflowLabel(students, allStudents)
        }

        if (lesson.event_type === 'presence') {
          lesson.title = this.$t('vue2.task_overview.work_hours')
          lesson.color = '#d9e9fb'
          lesson.teacher = ''
        }

        return lesson
      })
    }
  },
  methods: {
    getIsEventEditable (event) {
      return (this.calendarId * 1) === event.calendar_id && event.event_type !== 'presence'
    },
    getIsEventOtherSchool (event) {
      return event.event_type !== 'presence' && !this.userSchools[event.school_id]
    },
    sortEvents (events) {
      return events.sort((a, b) => {
        return moment(a.start).unix() - moment(b.start).unix()
      })
    },
    getShowDate (date) {
      return this.locale !== 'en' ? moment(date).locale(this.locale).format('ddd[.] DD/MM') : moment(date).locale(this.locale).format('ddd MM/DD')
    }
  }
}
</script>


<style scoped>
.d-cal-print {
  width: 100%;
  background: #fff;
  -webkit-print-color-adjust: exact !important;
}

.d-cal-print__cal {
  display: flex;
  border: 1px solid #d9d9d9;
}

.d-cal-print__cal__day {
  flex: 1;
  border: 0 solid #ebeff3;
  border-left-width: 1px;
  overflow: hidden;
}

.d-cal-print__cal__day__header {
  line-height: 24px;
  border-bottom: 1px solid #d9d9d9;
  text-align: center;
  font-weight: bold;
  text-transform: capitalize;
  margin-bottom: 1px;
}

.d-cal-print__event {
  color: #ffffff;
  font-size: 10px;
  margin: 0 2px;
  line-height: 1.2;
  border: 1px solid #ffffff;
  border-radius: 3px;
  text-align: left;
}

.flex.flex-1 li.d-cal-print__event {
  text-align: left;
}

.d-cal-print__event.d-cal-print__event--presence {
  color: #222;
}

.d-cal-print__event__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: rgb(34 34 34 / 10%);
  padding: 2px 3px;
  border-radius: 3px 3px 0 0;
}

.d-cal-print__event.d-cal-print__event--presence .d-cal-print__event__header {
  background: transparent;
  border: 1px solid #d9d9d9;
  border-radius: 3px;
}

.d-cal-print__event__title {
  font-weight: bold;
  flex: 1;
}

.d-cal-print__event__time {
  padding-left: 5px;
  font-size: 10px;
  min-width: 60px;
}

.d-cal-print__event__header + .day-cal__event__calendar {
  padding-top: 3px;
}

.d-cal-print__event__header + .day-cal__event__calendar {
  padding-top: 2px;
}

.day-cal__event__calendar {
  padding: 0 2px;
}

.d-cal-print__event > .day-cal__event__calendar:last-child {
  padding-bottom: 3px;
}

.d-cal-print__event__header + .day-cal__event__calendar {
  padding-top: 3px;
}

ul {
  margin: 0;
}

.text-explainer {
  margin-bottom: 0;
}

@media print {
  .d-cal-print__event {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
</style>



// WEBPACK FOOTER //
// DCalendarPrint.vue?862a240a